import { useMemo } from 'react';
import { EventSortOrder, SubscriptionFilters } from '~/components/sportsbook/lobby/api/sportsbook-api-types';
import useEventSubscription from '~/components/sportsbook/lobby/features/event-subscription/use-event-subscription';
import { RecommendedBetDto, RecommendedBetItemDto } from '~/components/sportsbook/lobby/api/sbapi/sbapi-api-model';
import useBetRecommendations from '~/components/sportsbook/lobby/hooks/bet-recommendations/use-bet-recommendations';

const useBetRecommendationsWithEventSubscription = (
	recommendationsKey: string,
	excludedSelectionIds: string[] = []
) => {
	const { recommendedBets, isLoading } = useBetRecommendations(recommendationsKey, excludedSelectionIds);

	const eventSubscriptionFilters = useMemo(() => {
		return {
			eventIds: recommendedBets?.flatMap(bet => bet.items.flatMap(item => item.event.id)),
			marketIds: recommendedBets?.flatMap(bet => bet.items.flatMap(item => item.market.id)),
			selectionIds: recommendedBets?.flatMap(bet => bet.items.flatMap(item => item.selection.id))
		} as SubscriptionFilters;
	}, [recommendedBets]);

	const { events = [], isInitialized: isEventsInitialized } = useEventSubscription({
		ref: `recommended-bets-carousel-events-${recommendationsKey}`,
		filters: eventSubscriptionFilters,
		isEnabled: recommendedBets !== undefined && recommendedBets?.length > 0,
		orderBy: EventSortOrder.NONE
	});

	const preparedRecommendedBets = useMemo(() => {
		return recommendedBets
			?.map(bet => ({
				...bet,
				items: [
					...bet.items.map(item => {
						const eventFromSubscription = events.find(event => event.id === item.event.id);
						const marketFromSubscription = eventFromSubscription?.markets?.find(
							market => market.id === item.market.id
						);
						const selectionFromSubscription = marketFromSubscription?.selections?.find(
							selection => selection.id === item.selection.id
						);

						return {
							...item,
							event: eventFromSubscription,
							market: marketFromSubscription,
							selection: selectionFromSubscription
						} as RecommendedBetItemDto;
					})
				]
			}))
			.filter(bet => bet.items.every(item => item.event && item.market && item.selection)) as RecommendedBetDto[];
	}, [recommendedBets, events]);

	return {
		recommendedBets: preparedRecommendedBets,
		isLoading: isLoading || (recommendedBets?.length > 0 && !isEventsInitialized)
	};
};

export default useBetRecommendationsWithEventSubscription;
